import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config/api.config";
import logger from "../utils/logger";
import {
  Check,
  ChevronDown,
  Menu,
  X,
  ArrowLeft,
  Shield,
  Users,
  FileText,
  Calendar,
  DollarSign,
  BarChart2,
  Lock,
  Code,
  LifeBuoy,
  Mail,
  ArrowUp,
  Loader,
  Phone,
  MessageCircle,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Helper component for FAQ items
const FaqItem = ({ question, answer, delay }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="accordion-item bg-white rounded-lg shadow-md mb-4 fade-in-section"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <button
        className="accordion-header w-full text-right p-6 font-bold text-lg text-[#1a3a64] flex justify-between items-center hover:text-[#d4af37] transition"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{question}</span>
        <ChevronDown
          className={`w-6 h-6 transform transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className="accordion-content overflow-hidden transition-all duration-300 ease-out"
        style={{ maxHeight: isOpen ? "1000px" : "0" }}
      >
        <div className="px-6 pb-6 text-gray-700">
          <p>{answer}</p>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // --- HERO SLIDER STATE ---
  const slides = [
    {
      image: "/images/hero-slide-1.jpg",
      title: "المرجع برو",
      description: "شريكك الذكي في إدارة مكتب المحاماة"
    },
    {
      image: "/images/hero-slide-2.jpg",
      title: "أمان وموثوقية",
      description: "حماية فائقة لبياناتك وبيانات موكليك"
    },
    {
      image: "/images/dashboard.png",
      title: "لوحة التحكم الشاملة",
      description: "تابع أداء مكتبك، المواعيد القادمة، والإحصائيات المالية في شاشة واحدة."
    },
    {
      image: "/images/client.png",
      title: "إدارة العملاء",
      description: "سجل متكامل لكل عميل يشمل القضايا، المدفوعات، والمستندات المرتبطة به."
    },
    {
      image: "/images/case.png",
      title: "ملفات القضايا",
      description: "أرشفة إلكترونية دقيقة لكافة تفاصيل القضايا والجلسات والقرارات."
    },
  ];
  const [currentSlide, setCurrentSlide] = useState(0);

  // --- SMART REDIRECT LOGIC ---
  useEffect(() => {
    // Check if user is currently logged in
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (token) {
      // User is logged in, redirect to dashboard
      logger.debug('✅ المستخدم مسجل دخول - التوجيه إلى الداشبورد');
      navigate('/dashboard', { replace: true });
      return;
    }

    // Check if user has ever logged in before
    const hasLoggedInBefore = localStorage.getItem('hasLoggedInBefore');
    
    if (hasLoggedInBefore === 'true') {
      // User has logged in before but is not currently logged in
      // Redirect to login page
      logger.debug('✅ المستخدم سجل دخول سابقاً - التوجيه إلى صفحة تسجيل الدخول');
      navigate('/login', { replace: true });
      return;
    }

    // New visitor, stay on landing page
    logger.debug('✅ زائر جديد - البقاء في صفحة الهبوط');
  }, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // --- SCROLL AND ANIMATION EFFECTS ---
  useEffect(() => {
    const handleScroll = () => {
      // Back to top button
      setShowBackToTop(window.scrollY > 200);
    };

    const sections = document.querySelectorAll(".fade-in-section");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    sections.forEach((section) => {
      observer.observe(section);
    });

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- FETCH SUBSCRIPTION PLANS ---
  useEffect(() => {
    logger.debug("🚀 useEffect للخطط بدأ بالتنفيذ");
    logger.debug("🔗 API_URL:", API_URL);

    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        logger.debug("🔄 بدء جلب الخطط من:", `${API_URL}/tenants/plans`);
        const response = await axios.get(`${API_URL}/tenants/plans`);
        logger.debug("📦 استجابة API الكاملة:", response);
        logger.debug("📦 بيانات API:", response.data);

        if (response.data.success && Array.isArray(response.data.data)) {
          logger.debug("✅ عدد الخطط المستلمة:", response.data.data.length);
          logger.debug("📋 الخطط الخام من API:", response.data.data);

          const formattedPlans = response.data.data.map((plan) => {
            logger.debug("🔄 معالجة خطة:", plan.name, plan.name_ar);
            const features = Array.isArray(plan.features) ? plan.features : [];
            const limits = plan.limits_json || {};
            const finalFeatures = [];

            if (limits.cases) {
              finalFeatures.push(
                limits.cases === -1
                  ? "عدد غير محدود من القضايا"
                  : `إدارة حتى ${limits.cases} قضية`
              );
            }
            if (limits.users) {
              finalFeatures.push(
                limits.users === -1
                  ? "عدد غير محدود من المستخدمين"
                  : `${limits.users} مستخدم`
              );
            }
            if (limits.clients) {
              finalFeatures.push(
                limits.clients === -1
                  ? "عدد غير محدود من العملاء"
                  : `${limits.clients} عميل`
              );
            }
            if (limits.storage_gb) {
              finalFeatures.push(`مساحة تخزين ${limits.storage_gb} جيجابايت`);
            }

            features.forEach((feature) => {
              if (typeof feature === "string") {
                finalFeatures.push(feature);
              }
            });

            const formattedPlan = {
              id: plan.id,
              name: plan.name_ar || plan.name,
              description: plan.description || "",
              price: parseFloat(plan.price) || 0,
              period: plan.billing_period === "monthly" ? "شهريًا" : "سنويًا",
              features: finalFeatures,
              isPopular:
                plan.name.toLowerCase() === "professional" || plan.id === 3,
              isContact: plan.name.toLowerCase() === "premium" || plan.id === 4,
            };
            logger.debug("📋 خطة منسقة:", formattedPlan);
            return formattedPlan;
          });
          logger.debug("✅ تم جلب الخطط:", formattedPlans);
          setPlans(formattedPlans);
          logger.debug("✅ تم تحديث state الخطط");
        } else {
          logger.warn("⚠️ لا توجد خطط في الاستجابة");
          setPlans([]);
        }
      } catch (error) {
        logger.error("❌ خطأ في جلب الخطط:", error);
        setPlans([]);
      } finally {
        setLoadingPlans(false);
        logger.debug("✅ انتهى تحميل الخطط، loadingPlans = false");
      }
    };

    fetchPlans();
    logger.debug("✅ تم استدعاء fetchPlans()");
  }, []);

  const handlePlanSelection = (planId) => {
    navigate("/register-tenant", { state: { selectedPlan: planId } });
  };

  const handleLinkClick = (e, targetId) => {
    e.preventDefault();
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  const navLinks = [
    { href: "#home", text: "الرئيسية" },
    { href: "#how-it-works", text: "كيف يعمل" },
    { href: "#features", text: "المميزات" },
    { href: "#pricing", text: "الأسعار" },
    { href: "#faq", text: "الأسئلة الشائعة" },
    { href: "#contact", text: "تواصل معنا" },
  ];

  const featureCards = [
    {
      icon: <Shield className="h-8 w-8" />,
      title: "إدارة القضايا",
      description:
        "أرشفة وتنظيم جميع القضايا، المستندات، الجلسات، والقرارات في مكان واحد آمن وسهل الوصول.",
      delay: 0,
    },
    {
      icon: <Calendar className="h-8 w-8" />,
      title: "جدولة المهام والمواعيد",
      description:
        "نظام تقويم ذكي لتنظيم مواعيد الجلسات والمهام مع إشعارات وتنبيهات لتجنب أي نسيان.",
      delay: 100,
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "إدارة العملاء والموكلين",
      description:
        "ملف خاص لكل عميل يحتوي على بياناته، قضاياه، والمستندات المرتبطة به لتسهيل التواصل والمتابعة.",
      delay: 200,
    },
    {
      icon: <DollarSign className="h-8 w-8" />,
      title: "الفوترة والأمور المالية",
      description:
        "إنشاء فواتير احترافية، تتبع المدفوعات، وإدارة أتعاب القضايا والمصروفات بكل سهولة ودقة.",
      delay: 300,
    },
    {
      icon: <FileText className="h-8 w-8" />,
      title: "مشاركة آمنة للمستندات",
      description:
        "مشاركة المستندات والملفات مع الموكلين أو فريق العمل بشكل آمن ومشفّر، مع تحديد صلاحيات الوصول.",
      delay: 400,
    },
    {
      icon: <BarChart2 className="h-8 w-8" />,
      title: "تقارير وتحليلات",
      description:
        "احصل على تقارير شاملة حول أداء المكتب، القضايا، والإيرادات لمساعدتك في اتخاذ قرارات مدروسة.",
      delay: 500,
    },
  ];

  const whyUsCards = [
    {
      icon: <Lock className="h-10 w-10" />,
      title: "أمان فائق للبيانات",
      description:
        "نستخدم أحدث تقنيات التشفير لضمان سرية وسلامة بيانات قضاياك وموكليك، مع خوادم مخصصة ومحمية.",
      delay: 100,
    },
    {
      icon: <Code className="h-10 w-10" />,
      title: "مصمم للمحامي العراقي",
      description:
        "تم تطوير النظام بالتشاور مع محامين عراقيين ليتوافق تمامًا مع إجراءات المحاكم والقوانين المحلية.",
      delay: 200,
    },
    {
      icon: <LifeBuoy className="h-10 w-10" />,
      title: "دعم فني متخصص",
      description:
        "فريق دعم فني متواجد لمساعدتك في أي وقت، وضمان استمرارية عملك دون أي عوائق تقنية.",
      delay: 300,
    },
  ];

  const faqItems = [
    {
      question: "هل بياناتي ومعلوماتي آمنة على النظام؟",
      answer:
        "بالتأكيد. أمان بياناتك هو أولويتنا القصوى. نستخدم بروتوكولات تشفير متقدمة (SSL) لحماية جميع البيانات المنقولة، بالإضافة إلى إجراءات أمان صارمة على خوادمنا لمنع أي وصول غير مصرح به.",
      delay: 0,
    },
    {
      question: "هل يمكنني استخدام النظام على الهاتف المحمول؟",
      answer:
        "نعم، تم تصميم النظام ليكون متجاوبًا بالكامل مع جميع الأجهزة. يمكنك الوصول إلى جميع مميزات النظام وإدارة مكتبك من أي مكان عبر هاتفك المحمول، جهازك اللوحي، أو الكمبيوتر.",
      delay: 100,
    },
    {
      question: "ماذا يحدث بعد انتهاء الفترة التجريبية المجانية؟",
      answer:
        "قبل انتهاء الفترة التجريبية، سنقوم بتذكيرك. سيكون لديك الخيار للاشتراك في إحدى خططنا المدفوعة للاستمرار في استخدام النظام بنفس البيانات التي أدخلتها. إذا قررت عدم الاشتراك، سيتم تعليق حسابك ولكننا سنحتفظ ببياناتك لفترة محدودة في حال غيرت رأيك.",
      delay: 200,
    },
    {
      question: "هل يمكنني إلغاء اشتراكي في أي وقت؟",
      answer:
        "نعم، يمكنك إلغاء اشتراكك الشهري في أي وقت تريده دون أي التزامات. ستتمكن من الوصول إلى حسابك حتى نهاية دورة الفوترة الحالية.",
      delay: 300,
    },
  ];

  return (
    <>
      {/* Google Fonts - Tajawal */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap"
        rel="stylesheet"
      />

      {/* Custom Styles */}
      <style>{`
        body {
          font-family: 'Tajawal', sans-serif;
          scroll-behavior: smooth;
        }
        
        /* Animation for fade-in on scroll */
        .fade-in-section {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .fade-in-section.is-visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Accordion styles for FAQ */
        .accordion-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease-out;
        }
      `}</style>

      <div
        className="bg-[#f8f9fa]"
        dir="rtl"
        style={{ fontFamily: "'Tajawal', sans-serif" }}
      >
        {/* Header & Navigation */}
        <header className="bg-white shadow-md sticky top-0 z-50">
          <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex flex-col">
              <a
                href="#home"
                onClick={(e) => handleLinkClick(e, "#home")}
                className="text-2xl font-bold text-[#1a3a64] leading-none"
              >
                <span className="text-[#d4af37]">المرجع</span> برو
              </a>
              <span className="text-[10px] md:text-xs text-[#1a3a64] mt-1 font-medium">
                من فوضى الأوراق... إلى قوة المرجع
              </span>
            </div>
            <div className="hidden md:flex space-x-6 space-x-reverse items-center">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleLinkClick(e, link.href)}
                  className="text-gray-700 hover:text-[#1a3a64] transition"
                >
                  {link.text}
                </a>
              ))}
            </div>
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate("/login")}
                className="text-gray-700 hover:text-[#1a3a64] transition font-medium"
              >
                تسجيل الدخول
              </button>
              <button
                onClick={() => navigate("/register-tenant")}
                className="bg-[#1a3a64] text-white py-2 px-5 rounded-lg hover:bg-opacity-90 transition"
              >
                ابدأ تجربتك المجانية
              </button>
            </div>
            <button
              id="mobile-menu-button"
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6 text-[#1a3a64]" />
              ) : (
                <Menu className="h-6 w-6 text-[#1a3a64]" />
              )}
            </button>
          </nav>
          {/* Mobile Menu */}
          <div
            id="mobile-menu"
            className={`md:hidden px-6 pb-4 ${isMenuOpen ? "block" : "hidden"}`}
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.href)}
                className="block py-2 text-gray-700 hover:text-[#1a3a64]"
              >
                {link.text}
              </a>
            ))}
            <button
              onClick={() => navigate("/login")}
              className="block w-full mt-2 text-center py-2 px-5 border border-[#1a3a64] text-[#1a3a64] rounded-lg hover:bg-gray-50 transition"
            >
              تسجيل الدخول
            </button>
            <button
              onClick={() => navigate("/register-tenant")}
              className="block w-full mt-2 bg-[#1a3a64] text-white text-center py-2 px-5 rounded-lg hover:bg-opacity-90"
            >
              ابدأ تجربتك المجانية
            </button>
          </div>
        </header>

        {/* Hero Section */}
        <main id="home" className="pt-16 pb-24 bg-white">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-extrabold text-[#1a3a64] mb-4 leading-tight">
                <span className="text-[#d4af37]">"المرجع برو"</span>
                النظام الأمثل لإدارة مكتب المحاماة الخاص بك في العراق
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8">
                نظّم قضاياك، تابع مهامك، وأدِر عملائك بكفاءة وأمان. "المرجع برو"
                مصمم خصيصًا ليتوافق مع متطلبات مهنة المحاماة في العراق.
              </p>
              <a
                href="#pricing"
                onClick={(e) => handleLinkClick(e, "#pricing")}
                className="inline-flex items-center justify-center bg-[#d4af37] text-[#1a3a64] font-bold py-3 px-10 rounded-lg hover:opacity-90 transition text-lg shadow-lg"
              >
                ابدأ بالخطة المجانية الآن
                <ArrowLeft className="h-6 w-6 mr-3" />
              </a>
            </div>
            <div className="mt-12 relative h-[300px] md:h-[500px] w-full max-w-5xl mx-auto rounded-lg shadow-2xl overflow-hidden bg-gray-100">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  className="absolute inset-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <img
                    src={slides[currentSlide].image}
                    alt={slides[currentSlide].title}
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient Overlay with Text */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-8 md:p-12 text-right">
                    <motion.h3 
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      className="text-[#d4af37] text-2xl md:text-3xl font-bold mb-3"
                    >
                      {slides[currentSlide].title}
                    </motion.h3>
                    <motion.p 
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                      className="text-white text-lg md:text-xl max-w-2xl"
                    >
                      {slides[currentSlide].description}
                    </motion.p>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Dots */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 space-x-reverse z-10">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-3 rounded-full transition-all duration-300 shadow-md ${
                      currentSlide === index
                        ? "bg-[#d4af37] w-8"
                        : "bg-white/70 w-3 hover:bg-white"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </main>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-20 bg-[#f8f9fa]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12 fade-in-section">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a64]">
                ابدأ العمل في 3 خطوات بسيطة
              </h2>
              <p className="text-gray-600 mt-2">
                انضم إلينا اليوم وعزز من إنتاجية مكتبك بسهولة.
              </p>
            </div>
            <div className="relative max-w-4xl mx-auto">
              <div
                className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-300"
                style={{ transform: "translateY(-50%)" }}
              ></div>
              <div className="grid md:grid-cols-3 gap-12 text-center relative">
                <div className="fade-in-section">
                  <div className="flex items-center justify-center h-24 w-24 bg-white border-2 border-[#1a3a64] text-[#1a3a64] rounded-full mx-auto mb-4 shadow-lg z-10 relative">
                    <span className="text-4xl font-bold">1</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a3a64] mb-2">
                    سجّل حسابك
                  </h3>
                  <p className="text-gray-600">
                    اختر الخطة التي تناسبك وأنشئ حسابك في أقل من دقيقة. ابدأ
                    تجربتك المجانية فورًا.
                  </p>
                </div>
                <div
                  className="fade-in-section"
                  style={{ transitionDelay: "200ms" }}
                >
                  <div className="flex items-center justify-center h-24 w-24 bg-white border-2 border-[#1a3a64] text-[#1a3a64] rounded-full mx-auto mb-4 shadow-lg z-10 relative">
                    <span className="text-4xl font-bold">2</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a3a64] mb-2">
                    أضف قضاياك وموكليك
                  </h3>
                  <p className="text-gray-600">
                    ابدأ بإدخال بيانات القضايا والموكلين بسهولة. نظّم مستنداتك
                    ومواعيدك في مكان واحد.
                  </p>
                </div>
                <div
                  className="fade-in-section"
                  style={{ transitionDelay: "400ms" }}
                >
                  <div className="flex items-center justify-center h-24 w-24 bg-white border-2 border-[#1a3a64] text-[#1a3a64] rounded-full mx-auto mb-4 shadow-lg z-10 relative">
                    <span className="text-4xl font-bold">3</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a3a64] mb-2">
                    ابدأ بالإدارة والتحكم
                  </h3>
                  <p className="text-gray-600">
                    تابع مهامك، أصدر الفواتير، وتعاون مع فريقك بكفاءة. استمتع
                    بقوة التنظيم الرقمي.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12 fade-in-section">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a64]">
                كل ما تحتاجه في منصة واحدة
              </h2>
              <p className="text-gray-600 mt-2">
                مميزات صُممت لتسهيل عملك اليومي وزيادة إنتاجيتك.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featureCards.map((feature) => (
                <div
                  key={feature.title}
                  className="bg-[#f8f9fa] p-8 rounded-lg shadow-lg text-center transform hover:-translate-y-2 transition-transform duration-300 fade-in-section"
                  style={{ transitionDelay: `${feature.delay}ms` }}
                >
                  <div className="flex items-center justify-center h-16 w-16 bg-[#1a3a64] text-[#d4af37] rounded-full mx-auto mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#1a3a64] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us Section */}
        <section id="why-us" className="py-20 bg-[#f8f9fa]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12 fade-in-section">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a64]">
                لماذا نظام المرجع برو هو خيارك الأفضل؟
              </h2>
              <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
                نحن ندرك التحديات التي تواجه المحامي العراقي، ولهذا قمنا بتصميم
                نظام يلبي احتياجاتك بدقة.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-10 max-w-5xl mx-auto">
              {whyUsCards.map((card) => (
                <div
                  key={card.title}
                  className="text-center fade-in-section"
                  style={{ transitionDelay: `${card.delay}ms` }}
                >
                  <div className="flex items-center justify-center h-20 w-20 bg-[#d4af37] text-[#1a3a64] rounded-full mx-auto mb-4 shadow-lg">
                    {card.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a3a64] mb-2">
                    {card.title}
                  </h3>
                  <p className="text-gray-600">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12 fade-in-section">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a64]">
                خطط تناسب الجميع، ابدأ مجاناً
              </h2>
              <p className="text-gray-600 mt-2">
                اختر الخطة التي تناسبك. يمكنك الترقية في أي وقت.
              </p>
            </div>
            {loadingPlans ? (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <Loader className="w-12 h-12 text-[#1a3a64] animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">جارٍ تحميل الخطط...</p>
                </div>
              </div>
            ) : plans.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                {plans.map((plan, index) => {
                  logger.debug(`🎨 Rendering plan ${index + 1}:`, plan);
                  return (
                    <div
                      key={plan.id}
                      className={`border rounded-lg p-8 flex flex-col bg-[#f8f9fa] transform hover:scale-105 transition-all duration-300 relative ${
                        plan.isPopular
                          ? "border-2 border-[#d4af37] shadow-2xl scale-105"
                          : "border-gray-200"
                      }`}
                    >
                      {plan.isPopular && (
                        <span className="bg-[#d4af37] text-[#1a3a64] text-xs font-bold px-3 py-1 rounded-full absolute -top-4 right-1/2 translate-x-1/2">
                          الأكثر شيوعًا
                        </span>
                      )}
                      <h3 className="text-2xl font-bold text-[#1a3a64] text-center">
                        {plan.name}
                      </h3>
                      <p className="text-gray-500 text-center mt-2 h-10">
                        {plan.description}
                      </p>
                      <div className="text-center my-6">
                        {plan.isContact ? (
                          <span className="text-4xl font-extrabold text-[#1a3a64]">
                            تواصل معنا
                          </span>
                        ) : (
                          <>
                            <span className="text-5xl font-extrabold text-[#1a3a64]">
                              {plan.price === 0
                                ? "٠"
                                : plan.price.toLocaleString("ar-IQ")}
                            </span>
                            <span className="text-gray-500">
                              {plan.price === 0
                                ? " د.ع / دائماً"
                                : ` د.ع / ${plan.period}`}
                            </span>
                          </>
                        )}
                      </div>
                      <ul className="space-y-4 mb-8 flex-grow">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center">
                            <Check className="w-6 h-6 text-green-500 ml-2 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => handlePlanSelection(plan.id)}
                        className={`w-full text-center font-bold py-3 px-6 rounded-lg transition ${
                          plan.isPopular
                            ? "bg-[#d4af37] text-[#1a3a64] hover:opacity-90"
                            : plan.isContact
                            ? "bg-gray-200 text-[#1a3a64] hover:bg-gray-300"
                            : "bg-[#1a3a64] text-white hover:bg-opacity-90"
                        }`}
                      >
                        {plan.isContact
                          ? "اطلب عرضًا"
                          : plan.price === 0
                          ? "سجل الآن مجاناً"
                          : "ابدأ التجربة المجانية"}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 px-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-2xl mx-auto">
                  <h3 className="text-xl font-semibold text-red-800 mb-2">
                    لا يمكن تحميل خطط الأسعار حالياً
                  </h3>
                  <p className="text-red-700">
                    حدث خطأ أثناء محاولة جلب خطط الأسعار. يرجى المحاولة مرة أخرى
                    لاحقاً.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-20 bg-[#f8f9fa]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12 fade-in-section">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a64]">
                أسئلة شائعة
              </h2>
              <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
                هل لديك استفسار؟ قد تجد إجابتك هنا.
              </p>
            </div>
            <div className="max-w-3xl mx-auto">
              {faqItems.map((item, index) => (
                <FaqItem
                  key={index}
                  question={item.question}
                  answer={item.answer}
                  delay={item.delay}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-[#1a3a64] text-white">
          <div className="container mx-auto px-6">
            <div className="text-center fade-in-section mb-12">
              <h2 className="text-3xl md:text-4xl font-bold">تواصل معنا</h2>
              <p className="mt-4 text-lg text-gray-300">
                نحن هنا لمساعدتك. تواصل معنا عبر أي من القنوات التالية
              </p>
            </div>

            {/* Contact Cards */}
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
              {/* WhatsApp Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center hover:bg-white/20 transition-all duration-300 transform hover:scale-105 fade-in-section">
                <div className="bg-[#25D366] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">واتساب</h3>
                <p className="text-gray-300 mb-4 text-sm">
                  تواصل معنا مباشرة عبر واتساب
                </p>
                <a
                  href="https://wa.me/9647889702444"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-[#25D366] text-white font-bold py-3 px-6 rounded-lg hover:opacity-90 transition"
                >
                  <div className="flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 ml-2" />
                    <span>راسلنا الآن</span>
                  </div>
                </a>
              </div>

              {/* Email Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center hover:bg-white/20 transition-all duration-300 transform hover:scale-105 fade-in-section">
                <div className="bg-[#d4af37] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#1a3a64]" />
                </div>
                <h3 className="text-xl font-bold mb-3">البريد الإلكتروني</h3>
                <p className="text-gray-300 mb-4 text-sm">
                  أرسل لنا استفسارك عبر البريد
                </p>
                <a
                  href="mailto:info@almarjeaPro.com"
                  className="inline-block bg-[#d4af37] text-[#1a3a64] font-bold py-3 px-6 rounded-lg hover:opacity-90 transition"
                >
                  <div className="flex items-center justify-center">
                    <Mail className="w-5 h-5 ml-2" />
                    <span>info@almarjeaPro.com</span>
                  </div>
                </a>
              </div>

              {/* Phone Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center hover:bg-white/20 transition-all duration-300 transform hover:scale-105 fade-in-section">
                <div className="bg-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">الهاتف</h3>
                <p className="text-gray-300 mb-4 text-sm">اتصل بنا مباشرة</p>
                <a
                  href="https://wa.me/9647889702444"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 text-white font-bold py-3 px-6 rounded-lg hover:opacity-90 transition"
                >
                  <div className="flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="currentColor"
                      className="bi bi-whatsapp ml-2"
                      viewBox="0 0 16 16"
                    >
                      <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
                    </svg>
                    <span dir="ltr">+964 788 970 2444</span>
                  </div>
                </a>
              </div>
            </div>

            {/* Social Media Links */}
            <div className="text-center fade-in-section">
              <h3 className="text-2xl font-bold mb-6">
                تابعنا على وسائل التواصل الاجتماعي
              </h3>
              <div className="flex justify-center gap-6 flex-wrap">
                {/* Facebook */}
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#1877F2] w-14 h-14 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                  aria-label="Facebook"
                >
                  <Facebook className="w-7 h-7 text-white" />
                </a>

                {/* Twitter */}
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#1DA1F2] w-14 h-14 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                  aria-label="Twitter"
                >
                  <Twitter className="w-7 h-7 text-white" />
                </a>

                {/* Instagram */}
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] w-14 h-14 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                  aria-label="Instagram"
                >
                  <Instagram className="w-7 h-7 text-white" />
                </a>

                {/* LinkedIn */}
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#0A66C2] w-14 h-14 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-7 h-7 text-white" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-10">
          <div className="container mx-auto px-6 text-center">
            <a
              href="#home"
              onClick={(e) => handleLinkClick(e, "#home")}
              className="text-2xl font-bold"
            >
              <span className="text-[#d4af37]">المرجع</span> برو
            </a>
            <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 mt-6">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleLinkClick(e, link.href)}
                  className="hover:text-[#d4af37] transition"
                >
                  {link.text}
                </a>
              ))}
            </div>
            <p className="mt-6 text-gray-400">
              &copy; {new Date().getFullYear()} المرجع برو. جميع الحقوق محفوظة.
            </p>
          </div>
        </footer>

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            id="back-to-top"
            onClick={scrollToTop}
            className="fixed bottom-8 left-8 bg-[#d4af37] text-[#1a3a64] p-3 rounded-full shadow-lg hover:bg-opacity-90 transition"
          >
            <ArrowUp className="h-6 w-6" />
          </button>
        )}
      </div>
    </>
  );
};

export default LandingPage;
